import React, { useMemo } from "react"
import { Box, Typography } from "@mui/material"
import { LegendSwatch } from "@/features/voyagePlanner/wizard/styles"
import { FIELD_LABEL_SX } from "@/theme/panelStyles"
import DecimalField from "@/components/ui/DecimalField"
import {
  nearestConsumptionRow,
  validateDecimal,
} from "@/features/voyagePlanner/voyageSettingsFields"

function DraftValueBox({ label, value }) {
  return (
    <Box>
      <Typography sx={FIELD_LABEL_SX}>{label}</Typography>
      <Box
        sx={{
          borderRadius: "8px",
          border: "1px solid #e2e8ee",
          bgcolor: "#f4f7fa",
          px: 1,
          py: 0.6,
          fontSize: "13px",
          fontWeight: 600,
          color: value != null ? "#5b7f9c" : "#a8b6c2",
        }}
      >
        {value != null ? String(value) : "—"}
      </Box>
    </Box>
  )
}

// M/E daily consumption table (read-only) + draft correction input.
function ConsumptionTab({
  consumptionProfileData,
  calculatorBasespeed,
  currentDraft,
  setCurrentDraft,
}) {
  const rows = useMemo(() => {
    const parsed = (consumptionProfileData?.values ?? [])
      .map((row) => ({
        knots: Number(row?.knots),
        consumption: Number(row?.consumption),
        type: row?.type,
      }))
      .filter(
        (row) =>
          Number.isFinite(row.knots) && Number.isFinite(row.consumption),
      )
      .sort((a, b) => a.knots - b.knots)

    const baseKnots =
      nearestConsumptionRow(Number(calculatorBasespeed), consumptionProfileData)
        ?.knots ?? null
    const max = Math.max(...parsed.map((row) => row.consumption), 1)

    return parsed.map((row) => {
      const isBase = row.knots === baseKnots
      const isEco = row.type === "ECO"
      const isMax = row.type === "MAX"
      return {
        ...row,
        isBase,
        barWidth: `${Math.max(1, Math.round((row.consumption / max) * 100))}%`,
        barColor: isBase
          ? "#004f7d"
          : isEco
            ? "#4caf78"
            : isMax
              ? "#e57368"
              : "#a8c3d6",
        bg: isBase
          ? "rgba(0,79,125,0.08)"
          : isEco
            ? "#e8f5e9"
            : isMax
              ? "#fdecea"
              : "#fff",
        fontWeight: isBase ? 700 : 400,
      }
    })
  }, [consumptionProfileData, calculatorBasespeed])

  const referenceDraft = Number(consumptionProfileData?.referenceDraft ?? 0)
  const draftDependency = Number(consumptionProfileData?.draftDependency ?? 0)
  const draftCorrectionAvailable = referenceDraft > 0 && draftDependency > 0
  const draftCorrectionActive =
    draftCorrectionAvailable && Number(currentDraft) > 0

  return (
    <>
      <Typography sx={{ fontSize: "10.5px", color: "#8a98a6", my: 1.25 }}>
        M/E daily consumption for fuel estimates and weather-based pathfinding
      </Typography>

      {setCurrentDraft && (
        <Box sx={{ mb: 1.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 0.75,
            }}
          >
            <DecimalField
              label="Current draft [m]"
              value={currentDraft}
              error={draftCorrectionAvailable && !draftCorrectionActive}
              onChange={(event) =>
                setCurrentDraft(validateDecimal(event.target.value).value)
              }
            />
            <DraftValueBox
              label="Reference draft [m]"
              value={referenceDraft > 0 ? referenceDraft : null}
            />
            <DraftValueBox
              label="Dependency [%/m]"
              value={draftDependency > 0 ? draftDependency : null}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.5,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: draftCorrectionActive ? "#2e7d32" : "#c4cdd5",
              }}
            />
            <Typography
              sx={{
                fontSize: "9.5px",
                color: draftCorrectionActive ? "#2e7d32" : "#8a98a6",
              }}
            >
              {draftCorrectionActive
                ? "Draft correction active"
                : "Enter current draft to activate draft correction"}
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          border: "1px solid #e2e8ee",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "62px 1fr 52px",
            fontSize: "10.5px",
            bgcolor: "#004f7d",
            color: "#fff",
            fontWeight: 700,
          }}
        >
          <Box sx={{ px: 1.25, py: 0.75 }}>kn</Box>
          <Box sx={{ px: 0.5, py: 0.75, textAlign: "center" }}>
            relative consumption
          </Box>
          <Box sx={{ px: 1.25, py: 0.75, textAlign: "right" }}>t/d</Box>
        </Box>
        {rows.map((row) => (
          <Box
            key={row.knots}
            sx={{
              display: "grid",
              gridTemplateColumns: "62px 1fr 52px",
              alignItems: "center",
              fontSize: "11px",
              bgcolor: row.bg,
              borderBottom: "1px solid #eef3f7",
            }}
          >
            <Box
              sx={{
                px: 1.25,
                py: 0.5,
                color: "#004f7d",
                fontWeight: row.fontWeight,
              }}
            >
              {row.knots}
            </Box>
            <Box sx={{ px: 0.5, py: 0.5 }}>
              <Box
                sx={{
                  height: 7,
                  borderRadius: "4px",
                  bgcolor: row.barColor,
                  width: row.barWidth,
                }}
              />
            </Box>
            <Box
              sx={{
                px: 1.25,
                py: 0.5,
                textAlign: "right",
                color: "#004f7d",
                fontWeight: row.fontWeight,
              }}
            >
              {row.consumption}
            </Box>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          fontSize: "9.5px",
          color: "#8a98a6",
          mt: 0.75,
        }}
      >
        <LegendSwatch color="#e8f5e9" label="Eco Speed" />
        <LegendSwatch color="rgba(0,79,125,0.08)" label="Base Speed" />
        <LegendSwatch color="#fdecea" label="Max Speed" />
      </Box>
      <Typography sx={{ fontSize: "9.5px", color: "#8a98a6", mt: 0.5 }}>
        Bar length shows each speed's consumption relative to the highest value
        in the profile.
      </Typography>
    </>
  )
}

export default React.memo(ConsumptionTab)
